import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import type { BinaryData } from '@ctw/shared/api/fhir/binaries';
import type { DocumentModel } from '@ctw/shared/api/fhir/models/document';
import { ContentError } from '@ctw/shared/components/errors/content-error';
import { LoadingSpinner } from '@ctw/shared/components/loading-spinner';
import { ReactCdaRenderer } from '@ctw/shared/content/document-viewer/_internal/renderers/react/react-cda-renderer';
import type { DocumentViewerControls } from '@ctw/shared/content/document-viewer/document-viewer';
import { SupportFormLink } from '@ctw/shared/content/support-form-link';
import { useFeatureFlag } from '@ctw/shared/context/feature-flag-provider';
import {
  DOCUMENT_RENDERER_CONTENT_TEST_ID,
  DOCUMENT_RENDERER_TIFF_IMAGE_TEST_ID,
  DOCUMENT_RENDERER_WEB_IMAGE_TEST_ID,
} from '@ctw/shared/test/ids';
import type { CdaDocument } from '@ctw/shared/utils/cda/types';
import { tw } from '@ctw/shared/utils/tailwind';
import UTIF from '@ctw/shared/vendor/utif';
import { type RefObject, memo, useEffect, useMemo, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();

export function tiffToPNG(
  arrayBuffer: ArrayBuffer,
  page = 0,
): {
  dataUrl: string;
  description: string;
  pageCount: number;
} {
  const imageFileDirectory = UTIF.decode(arrayBuffer);
  if (imageFileDirectory.length <= page) {
    throw new Error('Tiff page number requested is out of bounds');
  }
  const imageFileDirectoryPage = imageFileDirectory[page];
  // t270 is where an IFD indexes any description of the image
  if (!('t270' in imageFileDirectoryPage && Array.isArray(imageFileDirectoryPage.t270))) {
    imageFileDirectoryPage.t270 = ['']; // Set a default description
  }
  UTIF.decodeImage(arrayBuffer, imageFileDirectoryPage);
  const rgba = UTIF.toRGBA8(imageFileDirectory[0]); // Uint8Array with RGBA pixels

  const canvas = document.createElement('canvas');
  canvas.width = imageFileDirectoryPage.width;
  canvas.height = imageFileDirectoryPage.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Could not get 2d context');
  }

  const img = ctx.createImageData(canvas.width, canvas.height);
  img.data.set(rgba);
  ctx.putImageData(img, 0, 0);

  return {
    dataUrl: canvas.toDataURL(),
    description: `${imageFileDirectoryPage.t270[0]}`,
    pageCount: imageFileDirectory.length,
  };
}

const tiffTypes = ['/tif', '/tiff'];
const webImageTypes = ['/jpg', '/jpeg', '/png', '/apng', '/avif', '/gif', '/webp', '/svg+xml'];

const isSpecificContentType = (extensions: Array<string>, contentType: string) =>
  extensions.some((extension) => contentType.includes(extension));

interface DocumentRendererProps {
  controls: DocumentViewerControls;
  cdaDocument: CdaDocument | null | undefined;
  document: DocumentModel;
  binaryData: BinaryData;
  contentRef: RefObject<HTMLDivElement | null>;
}

export const DocumentRenderer = memo(
  ({ controls, cdaDocument, document, binaryData, contentRef }: DocumentRendererProps) => {
    const [arrayBuffer, setArrayBuffer] = useState<ArrayBuffer>();
    const [documentBlobUrl, setDocumentBlobUrl] = useState<string | undefined>(undefined);
    const [documentBlobNumPages, setDocumentBlobNumPages] = useState<number | null>(null);
    const [documentBlobRenderedPages, setDocumentBlobRenderedPages] = useState<number>(0);

    const useLegacyPDFRenderer = useFeatureFlag('ctw-legacy-pdf-renderer');

    // biome-ignore lint/correctness/useExhaustiveDependencies: Intentionally over-specific deps
    useEffect(() => {
      if (isSpecificContentType(tiffTypes, binaryData.binary.contentType)) {
        // Convert blob to an array buffer, which is used only for tiff files atm.
        void (async () => {
          setArrayBuffer(await binaryData.binary.blob.arrayBuffer());
        })();
      }
    }, [binaryData.binary.id, binaryData.binary.contentType]);

    const isPDF = useMemo(
      () => binaryData.binary.contentType.includes('/pdf'),
      [binaryData.binary.contentType],
    );
    const isWebImage = useMemo(
      () => isSpecificContentType(webImageTypes, binaryData.binary.contentType),
      [binaryData.binary.contentType],
    );

    useEffect(() => {
      let blobUrl = documentBlobUrl;
      if (!blobUrl && binaryData && (isPDF || isWebImage)) {
        blobUrl = URL.createObjectURL(binaryData.binary.blob);
        setDocumentBlobUrl(blobUrl);
      }

      return () => {
        // Revoke after a generous timeout to prevent revoking while the PDF viewer is rendering
        setTimeout(() => {
          if (blobUrl) {
            URL.revokeObjectURL(blobUrl);
          }
        }, 1000); // Needs to happen before browser runs out of memory, we're being extra safe. 500 still seems to work.
      };
    }, [documentBlobUrl, isPDF, isWebImage, binaryData]);

    if (!binaryData.binary) {
      return <></>;
    }

    if (isSpecificContentType(tiffTypes, binaryData.binary.contentType)) {
      if (arrayBuffer === undefined) {
        return <LoadingSpinner message="Loading image..." centered={true} />;
      }

      const { dataUrl, description } = tiffToPNG(arrayBuffer);
      return (
        <div
          ref={contentRef}
          className={tw`flex w-full flex-col items-center justify-center gap-2 p-5`}
          data-testid={DOCUMENT_RENDERER_CONTENT_TEST_ID}
        >
          <p className={tw`text-content-subtle text-sm leading-relaxed`}>{description}</p>
          <img
            key={document.id}
            src={dataUrl}
            alt={description}
            data-testid={DOCUMENT_RENDERER_TIFF_IMAGE_TEST_ID}
            className={tw`h-full max-h-[95vh] w-auto`}
          />
        </div>
      );
    }

    if (isWebImage && documentBlobUrl) {
      return (
        <div
          ref={contentRef}
          className={tw`flex w-full flex-col items-center justify-center gap-2 p-5`}
          data-testid={DOCUMENT_RENDERER_CONTENT_TEST_ID}
        >
          <img
            key={document.id}
            src={documentBlobUrl}
            alt=""
            aria-hidden="true"
            data-testid={DOCUMENT_RENDERER_WEB_IMAGE_TEST_ID}
            className={tw`h-full max-h-[95vh] w-auto object-contain`}
          />
        </div>
      );
    }

    if (isPDF && documentBlobUrl) {
      try {
        return (
          <div
            ref={contentRef}
            data-testid={DOCUMENT_RENDERER_CONTENT_TEST_ID}
            className={tw`relative w-full gap-2 overflow-auto p-3`}
          >
            {/* CTW-1803 - until we figure out why Athena CSP is rejecting the iframe, we'll use react-pdf just for Athena */}
            {useLegacyPDFRenderer ? (
              <>
                <Document
                  file={documentBlobUrl}
                  onLoadSuccess={({ numPages }) => setDocumentBlobNumPages(numPages)}
                >
                  {Array.from(new Array(documentBlobNumPages), (_element, index) => (
                    <Page
                      key={`page_${index + 1}`}
                      pageNumber={index + 1}
                      onRenderSuccess={() => setDocumentBlobRenderedPages((prev) => prev + 1)}
                    />
                  ))}
                </Document>
                {documentBlobRenderedPages < (documentBlobNumPages ?? 1) && (
                  <div className={tw`absolute top-0 right-0 h-full w-full`}>
                    <LoadingSpinner
                      centered={true}
                      message="Loading preview..."
                      className={tw`absolute top-0 left-0`}
                    />
                  </div>
                )}
              </>
            ) : (
              <iframe
                title="PDF Document"
                src={documentBlobUrl}
                className={tw`pdf-document h-full w-full rounded-md`}
              />
            )}
          </div>
        );
      } catch (_error) {
        return (
          <ContentError
            title="Unable to preview this PDF file in the web browser."
            className={tw`flex h-full max-h-full min-h-full w-full items-center justify-center`}
          >
            <span>If this problem persists </span>
            <SupportFormLink buttonText="contact support" />.
          </ContentError>
        );
      }
    }

    if (binaryData.binary.contentType.includes('xml')) {
      if (!cdaDocument) {
        return (
          <LoadingSpinner
            centered={true}
            message="Loading document..."
            className={tw`min-h-[20vh]`}
          />
        );
      }

      return (
        <ReactCdaRenderer
          key={document.id}
          controls={controls}
          contentRef={contentRef}
          cdaDocument={cdaDocument}
        />
      );
    }

    return (
      <ContentError
        title="Unable to preview this document."
        className={tw`flex h-full max-h-full min-h-full w-full items-center justify-center`}
      >
        <span>If this problem persists </span>
        <SupportFormLink buttonText="contact support" />.
      </ContentError>
    );
  },
  (prevProps, nextProps) => prevProps.document.id === nextProps.document.id,
);
